import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, HeartIcon, MenuIcon, SearchIcon, ShoppingBagIcon } from 'lucide-react';
import { mainNav } from '../../data/navigation';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { MegaMenu } from './MegaMenu';
import { MobileMenu } from './MobileMenu';
import { SearchOverlay } from './SearchOverlay';
const iconBtn = 'relative flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-150 hover:bg-shell focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay';
export function Header() {
  const {
    count,
    openCart
  } = useCart();
  const {
    ids
  } = useWishlist();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const closeTimer = useRef<number>();
  useEffect(() => {
    setMegaOpen(false);
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const openMega = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    setMegaOpen(true);
  }, []);
  const scheduleClose = useCallback(() => {
    closeTimer.current = window.setTimeout(() => setMegaOpen(false), 140);
  }, []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  return <header className={`sticky top-0 z-40 border-b bg-paper/95 backdrop-blur-sm transition-[border-color] duration-200 ${scrolled || megaOpen ? 'border-line' : 'border-transparent'}`} onKeyDown={e => e.key === 'Escape' && setMegaOpen(false)} data-id="element-396" data-mp="1dafsaardj63n">
      <div className="mx-auto grid h-16 max-w-[1440px] grid-cols-[1fr_auto_1fr] items-center px-3 md:h-20 md:px-8" data-id="element-397" data-mp="3jdbhc0ycchhz">
        <div className="flex items-center" data-id="element-398" data-mp="3ef9vx9dg063c">
          <button type="button" className={`${iconBtn} lg:hidden`} onClick={() => setMobileOpen(true)} aria-label="Ouvrir le menu" data-id="element-399" data-mp="2qodxhtehecn4">
            <MenuIcon className="h-5 w-5" data-id="element-400" data-mp="2mkzin62a42b9" />
          </button>
          <nav aria-label="Navigation principale" className="hidden lg:block" data-id="element-401" data-mp="30ez5g7g1otip">
            <ul className="flex items-center gap-8" data-id="element-402" data-mp="2vn8txy5dfzpp">
              {mainNav.map(item => <li key={item.to} data-id="element-403" data-mp="27x4m1el5hdsw">
                  <NavLink to={item.to} onMouseEnter={item.mega ? openMega : () => setMegaOpen(false)} onMouseLeave={item.mega ? scheduleClose : undefined} onFocus={item.mega ? openMega : () => setMegaOpen(false)} aria-expanded={item.mega ? megaOpen : undefined} className={({
                isActive
              }) => `flex items-center gap-1 py-2 text-sm transition-colors duration-150 hover:text-clay ${isActive ? 'font-medium text-ink' : 'text-ink/80'}`} data-id="element-404" data-mp="27w3c5dfvexcr">
                    {item.label}
                    {item.mega && <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ease-out ${megaOpen ? 'rotate-180' : ''}`} aria-hidden data-id="element-405" data-mp="s6kjuat3l9rl" />}
                  </NavLink>
                </li>)}
              <li data-id="element-406" data-mp="3hvmlx9ol44ne">
                <Link to="/boutique?derniers=1" onMouseEnter={() => setMegaOpen(false)} className="py-2 text-sm text-clay transition-colors duration-150 hover:text-clay-dark" data-id="element-407" data-mp="3hw60qhixucx7">
                  Derniers jours
                </Link>
              </li>
            </ul>
          </nav>
        </div>

        <Link to="/" className="whitespace-nowrap text-center font-display text-[22px] leading-none md:text-[30px]" aria-label="L’Éphémère Perpétuel, accueil" data-id="element-408" data-mp="3je4pke9nwv8k">
          L’Éphémère <em className="text-clay" data-id="element-409" data-mp="fxp90fv4jsol">Perpétuel</em>
        </Link>

        <div className="flex items-center justify-end" data-id="element-410" data-mp="fvbcs3opkafg">
          <button type="button" className={iconBtn} onClick={() => setSearchOpen(true)} aria-label="Rechercher" data-id="element-411" data-mp="ax9v9n6xkj63">
            <SearchIcon className="h-5 w-5" data-id="element-412" data-mp="5yghdtnqeq5e" />
          </button>
          <Link to="/favoris" className={`${iconBtn} hidden sm:flex`} aria-label={`Favoris (${ids.length})`} data-id="element-413" data-mp="2vp3ai86bqlub">
            <HeartIcon className="h-5 w-5" data-id="element-414" data-mp="2qp7hf3z60etj" />
            {ids.length > 0 && <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-clay" aria-hidden data-id="element-415" data-mp="2qrtnt0abkoa6" />}
          </Link>
          <button type="button" className={iconBtn} onClick={openCart} aria-label={`Panier (${count} article${count > 1 ? 's' : ''})`} data-id="element-416" data-mp="2lkdh4qozkq1p">
            <ShoppingBagIcon className="h-5 w-5" data-id="element-417" data-mp="2qionusjzikfp" />
            <AnimatePresence data-id="element-418" data-mp="2vag6mgwwflva">
              {count > 0 && <motion.span key={count} initial={{
              scale: 0.6,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} exit={{
              scale: 0.6,
              opacity: 0
            }} transition={{
              type: 'spring',
              stiffness: 600,
              damping: 22
            }} className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-clay px-1 text-[11px] font-semibold text-paper" aria-hidden data-id="element-419" data-mp="2vbzm45aqdsw0">
                  {count}
                </motion.span>}
            </AnimatePresence>
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-420" data-mp="1bu21fliw0518">
        {megaOpen && <MegaMenu onMouseEnter={openMega} onMouseLeave={scheduleClose} data-id="element-421" data-mp="1bv3n0jxj49x7" />}
      </AnimatePresence>
      <MobileMenu open={mobileOpen} onClose={closeMobile} data-id="element-422" data-mp="16x1w5ctv36b6" />
      <SearchOverlay open={searchOpen} onClose={closeSearch} data-id="element-423" data-mp="1ltaso6mdb5jw" />
    </header>;
}